/** How much of an unparseable body to quote before eliding the rest. */
const BODY_PREVIEW_LIMIT = 200;

/**
 * A response the client cannot make sense of: the request succeeded — the
 * status line says 2XX — but the body is not the `{ data: ... }` envelope every
 * Roboto API route returns.
 *
 * In practice this means the response came from something other than the API
 * handler: an SPA origin answering with `index.html`, a proxy's own page, an
 * empty body, or a payload from an incompatible API version.
 *
 * `message` is deliberately terse and free of response detail. Several error
 * surfaces render a raw `error.message` for any `Error` they are handed (see
 * `Datasets/Dataset/Dataset.tsx`), so anything put there is UI copy, shown to
 * whoever hit the failure. The diagnosis lives in `detail` and in the fields
 * below instead: `detail` is what gets logged, and error monitoring serializes
 * the fields off the error object without them ever being rendered.
 *
 * A dropped or aborted body read is NOT this error — that failure is rethrown
 * as itself, since the transport, not the payload, is what went wrong.
 */
export class UnexpectedResponseError extends Error {
  /** The full diagnosis: status, URL, and what the body actually was. For logs. */
  public readonly detail: string;
  public readonly status: number;
  public readonly url: string;
  public readonly contentType: string | null;
  /** The body as text, truncated. Empty when there was no body to read. */
  public readonly bodyPreview: string;

  /**
   * @param response - The response being rejected. Read for metadata only; its
   * body has already been consumed by the caller.
   * @param reason - What was wrong with the body, as a noun phrase completing
   * "...returned <reason>".
   * @param body - The raw response text, quoted into `bodyPreview`.
   */
  constructor(
    response: Response,
    reason: string,
    body: string,
    options?: { cause?: unknown },
  ) {
    super("Unexpected response from the Roboto API", options);

    const from = response.url ? ` from ${response.url}` : "";
    const statusText = response.statusText ? ` ${response.statusText}` : "";

    this.name = "UnexpectedResponseError";
    this.detail = `HTTP ${response.status}${statusText}${from} returned ${reason}`;
    this.status = response.status;
    this.url = response.url;
    this.contentType = response.headers.get("content-type");
    this.bodyPreview = truncate(body);
  }
}

/**
 * A single-line, length-capped rendering of a response body, for a log line
 * that has to stay readable in a console.
 */
export function truncate(body: string): string {
  const collapsed = body.replace(/\s+/g, " ").trim();
  return collapsed.length > BODY_PREVIEW_LIMIT
    ? `${collapsed.slice(0, BODY_PREVIEW_LIMIT)}…`
    : collapsed;
}
